class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.filter = this.querySelector('[data-predictive-search-filter]');
    this.productTypeInput = document.getElementById('product_type_input');
    this.STORAGE_KEY = 'm:search-product-type';
    this.defaultProductType = ''

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.filter && this.filter.addEventListener('change', this.onSelectChange.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const productTypeActive = window.localStorage.getItem(this.STORAGE_KEY) || this.defaultProductType;
    
    if (this.filter) {
      const allOptions = [...this.filter.options];
      let hasCurrentValue = false;
      allOptions && allOptions.forEach((option) => {
        if (option.value === productTypeActive) {
          hasCurrentValue = true;
        }
      });
      let currentValue = hasCurrentValue ? productTypeActive : this.defaultProductType;
      this.filter.value = currentValue
      this.productTypeInput.value = currentValue
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#m-predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;


    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onSelectChange(evt) {
    this.productTypeInput.value = evt.target.value;
    window.localStorage.setItem(this.STORAGE_KEY, evt.target.value);
    const query = this.getQuery();
    if (query.length > 0) {
      this.getSearchResults(query);
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
      //   // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
      //   return;
      // }
      
      const newButtonText = currentButtonText.replace(previousTerm, newTerm).replace(`product_type:${this.productTypeInput.value} AND `, '');
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.m-predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    let searchParam = '';
    if (this.productTypeInput && this.productTypeInput.value !== '') {
      searchParam = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
    } else {
      searchParam = `q=${encodeURIComponent(searchTerm)}`
    }

    const queryKey = searchParam.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${window.MinimogSettings.routes.predictive_search_url}?${searchParam}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.m-predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    // this.setAttribute('loading', true);
    this.classList.add('m-spinner-loading');
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }


renderSearchResults(resultsMarkup) {
  this.predictiveSearchResults.innerHTML = resultsMarkup;

  const term = this.getQuery();
  this.updateSearchForTerm(this.searchTerm, term);
  this.setAttribute('results', true);
  this.setLiveRegionResults();
  this.open();

  const isArabic = window.MinimogSettings.shop_locale.current
    .toLowerCase().startsWith('ar');

  if (!isArabic) return;

  const container = this.predictiveSearchResults;
  const root = window.Shopify.routes.root;
  const token = this._arToken = (this._arToken || 0) + 1;

  // تعريب عناوين القائمة
  container.querySelectorAll('h2, h3').forEach(el => {
    if (el.textContent.trim() === 'Products') {
      el.textContent = 'المنتجات';
    }
  });

  const searchFor = container.querySelector(
    '[data-predictive-search-search-for-text]'
  );

  if (searchFor) {
    searchFor.textContent = `البحث عن "${term}"`;
  }

  // الاحتفاظ بالنتائج المترجمة لتسريع البحث
  this._arTitleCache ||= new Map();

  container.querySelectorAll('a[href*="/products/"]').forEach(link => {
    const original = new URL(link.href, location.origin);
    const match = original.pathname.match(/\/products\/([^/]+)/);
    if (!match) return;

    const handle = decodeURIComponent(match[1]);

    // الرابط العربي
    link.href = `${root}products/${encodeURIComponent(handle)}${original.search}`;

    if (!this._arTitleCache.has(handle)) {
      const getProduct = async (prefix) => {
        const response = await fetch(
          `${prefix}products/${encodeURIComponent(handle)}.js`
        );

        if (!response.ok) throw new Error(response.status);
        return response.json();
      };

      const promise = Promise.all([
        getProduct(root),
        getProduct('/en/')
      ]).then(([arabic, english]) => ({
        arabic,
        english
      })).catch(error => {
        this._arTitleCache.delete(handle);
        console.warn('Arabic search translation:', error);
        return null;
      });

      this._arTitleCache.set(handle, promise);
    }

    this._arTitleCache.get(handle).then(data => {
      if (!data || !link.isConnected || this._arToken !== token) {
        return;
      }

      const { arabic, english } = data;

      if (!arabic.title || !english.title) return;

      // تحديث رابط المنتج إلى الرابط المحلي الصحيح
      if (arabic.url) {
        const target = new URL(arabic.url, location.origin);
        target.search = original.search;
        link.href = target.href;
      }

      if (arabic.title === english.title) return;

      // تحديد عنوان المنتج دون الاعتماد على أسماء CSS
      const card = link.closest('li') || link;
      const walker = document.createTreeWalker(
        card,
        NodeFilter.SHOW_TEXT
      );

      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.nodeValue?.includes(english.title)) {
          node.nodeValue = node.nodeValue.replace(
            english.title,
            arabic.title
          );
        }
      }

      const image = card.querySelector('img');
      if (image?.alt === english.title) {
        image.alt = arabic.title;
      }
    });
  });

  // اقتراحات بحث عربية تلقائية من أسماء المنتجات المترجمة
  (() => {
    const region = this.predictiveSearchResults;

    const heading = [...region.querySelectorAll(
      'h2,h3,h4,h5,span,strong,div'
    )].find(el =>
      el.children.length === 0 &&
      /^(suggestions|اقتراحات البحث)$/i.test(el.textContent.trim())
    );

    if (!heading) return;

    const isSearchLink = link => {
      try {
        return /\/search\/?$/.test(
          new URL(link.href, location.origin).pathname
        );
      } catch {
        return false;
      }
    };

    let group = heading.parentElement;

    while (
      group &&
      group !== region &&
      ![...group.querySelectorAll('a[href]')].some(isSearchLink)
    ) {
      group = group.parentElement;
    }

    if (!group || group === region) return;

    const links = [...group.querySelectorAll('a[href]')]
      .filter(isSearchLink);

    if (!links.length) return;

    heading.textContent = 'اقتراحات البحث';
    heading.dir = 'rtl';

    // إخفاء الاقتراحات الإنجليزية حتى تجهيز العربية
    links.forEach(link => {
      link.style.visibility = 'hidden';
    });

    const root = window.Shopify.routes.root;
    const query = this.getQuery();

    const handles = [...new Set(
      [...region.querySelectorAll('a[href*="/products/"]')]
        .map(link => {
          const match = new URL(
            link.href, location.origin
          ).pathname.match(/\/products\/([^/]+)/);

          return match ? decodeURIComponent(match[1]) : null;
        })
        .filter(Boolean)
    )].slice(0, 10);

    const getTitle = handle => {
      const cached = this._arTitleCache?.get(handle);

      if (cached) {
        return cached.then(data => data?.arabic?.title);
      }

      return fetch(
        `${root}products/${encodeURIComponent(handle)}.js`
      )
      .then(response => response.ok ? response.json() : null)
      .then(product => product?.title);
    };

    Promise.allSettled(handles.map(getTitle)).then(results => {
      if (
        !region.contains(heading) ||
        this.getQuery() !== query
      ) return;

      const normalize = text => text
        .normalize('NFKC')
        .replace(/[\u064B-\u065F\u0640]/g, '')
        .replace(/[إأآ]/g, 'ا')
        .replace(/ى/g, 'ي')
        .toLowerCase();

      const keyword = normalize(query);
      const phrases = new Set();

      const titles = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value)
        .filter(Boolean);

      // استخراج عبارات مرتبطة بالبحث من أسماء المنتجات العربية
      for (const title of titles) {
        const words = title
          .split(/[\s،,|/()\-–]+/)
          .filter(word =>
            /[\u0621-\u064A]/.test(word) &&
            word.length > 1
          );

        if (words.length < 2) continue;

        const position = words.findIndex(word =>
          normalize(word).includes(keyword)
        );

        const start = position >= 0 ? position : 0;
        const phrase = words.slice(start, start + 3).join(' ');

        if (phrase) phrases.add(phrase);
      }

      // إنتاج صيغ أقصر عند الحاجة
      for (const title of titles) {
        if (phrases.size >= links.length) break;

        const words = title
          .split(/[\s،,|/()\-–]+/)
          .filter(word =>
            /[\u0621-\u064A]/.test(word) &&
            word.length > 1
          );

        const position = words.findIndex(word =>
          normalize(word).includes(keyword)
        );

        const start = position >= 0 ? position : 0;
        const phrase = words.slice(start, start + 2).join(' ');

        if (phrase) phrases.add(phrase);
      }

      const suggestions = [...phrases].slice(0, links.length);

      if (!suggestions.length) {
        group.hidden = true;
        return;
      }

      links.forEach((link, index) => {
        const phrase = suggestions[index];

        if (!phrase) {
          const item = link.closest('li');
          if (item) item.remove();
          else link.hidden = true;
          return;
        }

        const url = new URL(
          window.MinimogSettings.routes.search_url,
          location.origin
        );

        url.searchParams.set('q', phrase);
        url.searchParams.set('type', 'product');

        link.href = url.pathname + url.search;
        link.dir = 'rtl';
        link.lang = 'ar';
        link.setAttribute('aria-label', phrase);

        // المحافظة على عناصر القالب الأصلية
        const walker = document.createTreeWalker(
          link,
          NodeFilter.SHOW_TEXT
        );

        const nodes = [];

        while (walker.nextNode()) {
          nodes.push(walker.currentNode);
        }

        if (nodes.length) {
          nodes[0].nodeValue = phrase;

          nodes.slice(1).forEach(node => {
            node.nodeValue = '';
          });
        } else {
          link.textContent = phrase;
        }

        link.style.visibility = '';
      });
    });
  })();
}


  setLiveRegionResults() {
    // this.removeAttribute('loading');
    this.classList.remove('m-spinner-loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.m-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.setProperty('--search-result-max-height', `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`);
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);